import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, Bell, CheckCircle, Loader2, Save } from 'lucide-react'
import api from '../../utils/api'

const defaultScopeConfig = {
  mode: 'off',
  phones: [],
  forcePhones: [],
  width: 390,
  height: 270
}

const defaultCampaignConfig = {
  enabled: true,
  minVersion: '1.3.16',
  fixedTokenAmountRmb: 5,
  activatedLowCostBalanceThresholdRmb: 4,
  activatedLowCostStopConsumedAmountRmb: 1,
  lowBalanceThresholdRmb: 0.1,
  minCashbackAmountRmb: 0.01,
  pointsPerRmbSettingKey: 'points_per_rmb',
  freePointsTypes: ['daily_free', 'legacy', 'admin', 'reminder_bonus'],
  priority: ['low_balance_unpaid', 'activated_low_cost_15d', 'unactivated'],
  routes: {
    unactivated: {
      stage1DelayHours: 24,
      stage2DelayHours: 72,
      stage3DelayHours: 72,
      pageKeys: { 1: 'page1', 2: 'page2', 3: 'page3' }
    },
    activated_low_cost_15d: {
      stage1DelayDays: 15,
      stage2DelayHours: 72,
      stage3DelayHours: 72,
      pageKeys: { 1: 'page1', 2: 'page2', 3: 'page3' }
    },
    low_balance_unpaid: {
      stage1DelayDays: 7,
      stage2DelayDays: 7,
      stage3DelayDays: 7,
      pageKeys: { 1: 'page4', 2: 'page4', 3: 'page4' }
    }
  },
  pages: {
    page1: { title: '页面1', html: '<!doctype html><html><head><meta charset="utf-8"><title>page1</title></head><body><div>页面1</div></body></html>' },
    page2: { title: '页面2', html: '<!doctype html><html><head><meta charset="utf-8"><title>page2</title></head><body><div>页面2</div></body></html>' },
    page3: { title: '页面3', html: '<!doctype html><html><head><meta charset="utf-8"><title>page3</title></head><body><div>页面3</div></body></html>' },
    page4: { title: '页面4', html: '<!doctype html><html><head><meta charset="utf-8"><title>page4</title></head><body><div>页面4</div></body></html>' }
  }
}

function normalizePhoneList(values) {
  if (!Array.isArray(values)) return []
  return Array.from(
    new Set(
      values
        .map((item) => String(item || '').trim())
        .filter(Boolean)
    )
  )
}

function parseScopeConfig(rawValue) {
  if (!rawValue || !String(rawValue).trim()) {
    return { ...defaultScopeConfig }
  }

  try {
    const parsed = JSON.parse(String(rawValue))
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return {
        mode: ['off', 'whitelist', 'all'].includes(String(parsed.mode || '').trim())
          ? String(parsed.mode || '').trim()
          : 'off',
        phones: normalizePhoneList(parsed.phones),
        forcePhones: normalizePhoneList(parsed.forcePhones),
        width: Number(parsed.width || defaultScopeConfig.width) || defaultScopeConfig.width,
        height: Number(parsed.height || defaultScopeConfig.height) || defaultScopeConfig.height
      }
    }
  } catch (error) {
    console.error('解析 reminder_status 失败:', error)
  }

  return { ...defaultScopeConfig }
}

function parseCampaignConfig(rawValue) {
  if (!rawValue || !String(rawValue).trim()) {
    return { ...defaultCampaignConfig }
  }

  try {
    const parsed = JSON.parse(String(rawValue))
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return {
        ...defaultCampaignConfig,
        ...parsed,
        routes: {
          ...defaultCampaignConfig.routes,
          ...(parsed.routes || {})
        },
        pages: {
          ...defaultCampaignConfig.pages,
          ...(parsed.pages || {})
        }
      }
    }
  } catch (error) {
    console.error('解析 reminder_campaign_v2 失败:', error)
  }

  return { ...defaultCampaignConfig }
}

export default function UserReminderSettingsTab() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [successMsg, setSuccessMsg] = useState('')
  const [form, setForm] = useState({
    mode: 'off',
    phonesText: '',
    forcePhonesText: '',
    width: '390',
    height: '270',
    campaignJson: JSON.stringify(defaultCampaignConfig, null, 2)
  })

  useEffect(() => {
    fetchSettings()
  }, [])

  const previewPhones = useMemo(
    () => normalizePhoneList(String(form.phonesText || '').split(/[\n,，]/)),
    [form.phonesText]
  )
  const previewForcePhones = useMemo(
    () => normalizePhoneList(String(form.forcePhonesText || '').split(/[\n,，]/)),
    [form.forcePhonesText]
  )

  const fetchSettings = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await api.get('/admin/settings')
      if (response.data.success) {
        const data = response.data || {}
        const rawScopeValue = typeof data.reminder_status === 'object'
          ? data.reminder_status?.value
          : data.reminder_status
        const rawCampaignValue = typeof data.reminder_campaign_v2 === 'object'
          ? data.reminder_campaign_v2?.value
          : data.reminder_campaign_v2
        const scopeConfig = parseScopeConfig(rawScopeValue)
        const campaignConfig = parseCampaignConfig(rawCampaignValue)
        setForm({
          mode: scopeConfig.mode,
          phonesText: scopeConfig.phones.join('\n'),
          forcePhonesText: scopeConfig.forcePhones.join('\n'),
          width: String(scopeConfig.width || defaultScopeConfig.width),
          height: String(scopeConfig.height || defaultScopeConfig.height),
          campaignJson: JSON.stringify(campaignConfig, null, 2)
        })
      }
    } catch (err) {
      setError(err.response?.data?.error || '获取用户弹窗设置失败')
    } finally {
      setLoading(false)
    }
  }

  const handleFieldChange = (event) => {
    const { name, value } = event.target
    setForm((prev) => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')
    setSuccessMsg('')

    try {
      const parsedCampaign = JSON.parse(String(form.campaignJson || '{}'))
      const payload = {
        reminder_status: JSON.stringify({
          mode: form.mode,
          phones: previewPhones,
          forcePhones: previewForcePhones,
          width: Number(form.width || defaultScopeConfig.width) || defaultScopeConfig.width,
          height: Number(form.height || defaultScopeConfig.height) || defaultScopeConfig.height
        }),
        reminder_campaign_v2: JSON.stringify(parsedCampaign)
      }

      const response = await api.post('/admin/settings', { settings: payload })
      if (response.data.success) {
        setSuccessMsg('用户弹窗设置更新成功')
        setTimeout(() => setSuccessMsg(''), 3000)
      }
    } catch (err) {
      const message = err instanceof SyntaxError
        ? '活动配置 JSON 格式无效'
        : (err.response?.data?.error || '用户弹窗设置更新失败')
      setError(message)
      setTimeout(() => setError(''), 4000)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-8">
      {error ? (
        <div className="rounded-xl bg-red-50 p-4 text-red-600 dark:bg-red-900/30 dark:text-red-400">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            <p>{error}</p>
          </div>
        </div>
      ) : null}

      {successMsg ? (
        <div className="rounded-xl bg-green-50 p-4 text-green-600 dark:bg-green-900/30 dark:text-green-400">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            <p>{successMsg}</p>
          </div>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">用户弹窗设置</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">保留弹窗总开关和白名单，同时新增本次 reminder v2 活动 JSON 配置。</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            保存配置
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="space-y-6 p-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">弹窗模式</label>
                <select
                  name="mode"
                  value={form.mode}
                  onChange={handleFieldChange}
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                >
                  <option value="off">全部关闭</option>
                  <option value="whitelist">仅白名单手机号弹窗</option>
                  <option value="all">全部允许弹窗</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">弹窗宽度</label>
                <input
                  name="width"
                  type="number"
                  value={form.width}
                  onChange={handleFieldChange}
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">弹窗高度</label>
                <input
                  name="height"
                  type="number"
                  value={form.height}
                  onChange={handleFieldChange}
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300">
                当前识别白名单 {previewPhones.length} 个，强制弹窗 {previewForcePhones.length} 个
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">白名单手机号</label>
              <textarea
                name="phonesText"
                value={form.phonesText}
                onChange={handleFieldChange}
                rows={4}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                placeholder="每行一个手机号，也支持逗号分隔"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">强制弹窗手机号</label>
              <textarea
                name="forcePhonesText"
                value={form.forcePhonesText}
                onChange={handleFieldChange}
                rows={4}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                placeholder="命中后无视路线判断，直接走 page1"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">活动配置 JSON</label>
              <textarea
                name="campaignJson"
                value={form.campaignJson}
                onChange={handleFieldChange}
                rows={34}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 font-mono text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                placeholder="请输入 reminder_campaign_v2 的 JSON"
              />
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                这里控制 page1/page2/page3/page4 的 HTML、路线优先级、延迟时间、金额阈值和免费提现相关参数。
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
